// Firestore service layer — all database operations
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    serverTimestamp,
    increment,
    runTransaction,
    writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface QuotationItem {
    sNo: number;
    productId?: string;
    description: string;
    brand?: string;
    itemCode?: string;
    hsCode?: string;
    unitRate: string | number; // allowing text like 'NILL' or numeric
    quantity: number;
    uom?: string; // e.g. 'PCS', 'MTR', 'KG', 'SET', 'BOX'
    taxRate?: number;
    discount?: number;
    total: string | number;
}

export interface Quotation {
    id?: string;
    quotationNumber: string;
    date: string;
    validUntil?: string;
    customerId?: string;
    customerName?: string;
    companyName: string;
    companyAddress?: string;
    customerPhone?: string;
    customerEmail?: string;
    customerNtn?: string;
    customerStn?: string;
    attnName1?: string;
    attnRole1?: string;
    attnName2?: string;
    attnRole2?: string;
    subject: string;
    items: QuotationItem[];
    subtotal: number;
    taxRate?: number;
    taxAmount: number;
    discountAmount: number;
    totalAmount: number;
    status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'converted';
    notes?: string;
    termsAndConditions?: string;
    convertedOrderId?: string;
    createdBy?: string;
    createdAt?: any;
    updatedAt?: any;
}


export interface Category {
    id?: string;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    sortOrder: number;
    isActive: boolean;
    createdAt?: any;
    updatedAt?: any;
}

export interface Product {
    id?: string;
    categoryId?: string;
    name: string;
    code: string;
    hsCode?: string;
    taxRate?: number;
    description?: string;
    brand?: string;
    modelNumber?: string;
    price: number;
    costPrice: number;
    unit: string;
    weight?: string;
    warrantyInfo?: string;
    stock: number;
    minStock: number;
    isAvailable: boolean;
    imageUrl?: string;
    createdAt?: any;
    updatedAt?: any;
}

export interface Customer {
    id?: string;
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    ntn?: string;
    stn?: string;
    cnic?: string;
    type: 'walk-in' | 'regular' | 'wholesale' | 'dealer';
    creditLimit?: number;
    balance: number;
    isActive: boolean;
    createdAt?: any;
    updatedAt?: any;
}

export interface Supplier {
    id?: string;
    name: string;
    company?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    ntn?: string;
    contactPerson?: string;
    balance: number;
    isActive: boolean;
    createdAt?: any;
    updatedAt?: any;
}

export interface OrderItem {
    productId: string;
    name: string;
    code: string;
    hsCode?: string;
    uom?: string;
    quantity: number;
    unitPrice: number;
    total: number;
    taxRate: number;
    taxAmount: number;
}

export interface Order {
    id?: string;
    orderNumber: string;
    docNumber?: string;
    poNumber?: string;
    deliveryNumber?: string;
    invoiceType?: 'general' | 'fbr';
    customerId?: string;
    customerName?: string;
    customerAddress?: string;
    customerCity?: string;
    customerNtn?: string;
    customerStn?: string;
    customerCnic?: string;
    customerPhone?: string;
    createdBy: string;
    status: 'pending' | 'completed' | 'cancelled' | 'returned';
    items: OrderItem[];
    subtotal: number;
    taxRate: number;
    taxAmount: number;
    discountAmount: number;
    totalAmount: number;
    paymentMethod: 'cash' | 'credit_card' | 'debit_card' | 'bank_transfer';
    fbrStatus?: string;
    fbrInvoiceNumber?: string;
    fbrPayload?: any;
    invoiceNumber?: string;
    date?: string;
    invoiceDate?: string;
    notes?: string;
    createdAt?: any;
    updatedAt?: any;
    completedAt?: any;
}

export interface Payment {
    id?: string;
    orderId: string;
    paymentMethod: 'cash' | 'credit_card' | 'bank_transfer';
    amount: number;
    referenceNumber?: string;
    status: 'pending' | 'completed' | 'failed' | 'refunded';
    processedBy: string;
    createdAt?: any;
}

export interface FBRInvoiceRecord {
    id?: string;
    orderId: string;
    invoiceNumber: string;
    fbrInvoiceNumber?: string;
    posId?: string;
    usid?: string;
    buyerNtn?: string;
    buyerCnic?: string;
    buyerName?: string;
    buyerPhone?: string;
    totalSaleValue: number;
    totalTaxCharged: number;
    totalQuantity: number;
    status: 'pending' | 'submitted' | 'accepted' | 'rejected' | 'error';
    errorMessage?: string;
    submittedAt?: any;
    createdAt?: any;
    updatedAt?: any;
}

// ======================== CATEGORIES ========================

export const categoriesRef = collection(db, 'categories');

export async function getCategories(): Promise<Category[]> {
    const q = query(categoriesRef, orderBy('sortOrder'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Category));
}

export async function createCategory(data: Omit<Category, 'id'>): Promise<string> {
    const docRef = await addDoc(categoriesRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return docRef.id;
}

export async function updateCategory(id: string, data: Partial<Category>): Promise<void> {
    await updateDoc(doc(db, 'categories', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCategory(id: string): Promise<void> {
    await deleteDoc(doc(db, 'categories', id));
}

// ======================== PRODUCTS ========================

export const productsRef = collection(db, 'products');

export async function getProducts(filters?: { categoryId?: string; available?: boolean }): Promise<Product[]> {
    const snap = await getDocs(query(productsRef, orderBy('name')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Product));
    if (filters?.categoryId) docs = docs.filter(d => d.categoryId === filters.categoryId);
    if (filters?.available !== undefined) docs = docs.filter(d => d.isAvailable === filters.available);
    return docs;
}

export async function getProduct(id: string): Promise<Product | null> {
    const snap = await getDoc(doc(db, 'products', id));
    return snap.exists() ? { id: snap.id, ...snap.data() } as Product : null;
}

export async function createProduct(data: Omit<Product, 'id'>): Promise<string> {
    const docRef = await addDoc(productsRef, {
        ...data,
        isAvailable: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return docRef.id;
}

export async function updateProduct(id: string, data: Partial<Product>): Promise<void> {
    await updateDoc(doc(db, 'products', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteProduct(id: string): Promise<void> {
    await deleteDoc(doc(db, 'products', id));
}

// ======================== CUSTOMERS ========================

export const customersRef = collection(db, 'customers');

export async function getCustomers(filters?: { type?: string }): Promise<Customer[]> {
    const snap = await getDocs(query(customersRef, orderBy('name')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Customer));
    if (filters?.type) docs = docs.filter(d => d.type === filters.type);
    return docs;
}

export async function createCustomer(data: Omit<Customer, 'id'>): Promise<string> {
    const docRef = await addDoc(customersRef, {
        ...data,
        balance: data.balance || 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return docRef.id;
}

export async function updateCustomer(id: string, data: Partial<Customer>): Promise<void> {
    await updateDoc(doc(db, 'customers', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCustomer(id: string): Promise<void> {
    await deleteDoc(doc(db, 'customers', id));
}

// ======================== SUPPLIERS ========================

export const suppliersRef = collection(db, 'suppliers');

export async function getSuppliers(): Promise<Supplier[]> {
    const q = query(suppliersRef, orderBy('name'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Supplier));
}

export async function createSupplier(data: Omit<Supplier, 'id'>): Promise<string> {
    const docRef = await addDoc(suppliersRef, {
        ...data,
        balance: data.balance || 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return docRef.id;
}

export async function updateSupplier(id: string, data: Partial<Supplier>): Promise<void> {
    await updateDoc(doc(db, 'suppliers', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteSupplier(id: string): Promise<void> {
    await deleteDoc(doc(db, 'suppliers', id));
}

// ======================== ORDERS ========================

export const ordersRef = collection(db, 'orders');

export async function getOrders(filters?: { status?: string }): Promise<Order[]> {
    const snap = await getDocs(query(ordersRef, orderBy('createdAt', 'desc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Order));
    if (filters?.status) docs = docs.filter(d => d.status === filters.status);
    return docs;
}

export async function createOrder(data: Omit<Order, 'id'>): Promise<string> {
    const orderRef = doc(collection(db, 'orders'));

    // Harmonize date and invoice numbers
    const effectiveDateStr = data.invoiceDate || data.date;
    const finalInvoiceNumber = data.invoiceNumber || data.orderNumber;
    const finalOrderNumber = data.orderNumber || data.invoiceNumber;

    let createdAtValue = data.createdAt;
    if (!createdAtValue && effectiveDateStr) {
        const parts = effectiveDateStr.split('-').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
            createdAtValue = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
        }
    }
    if (!createdAtValue) {
        createdAtValue = serverTimestamp();
    }

    const batch = writeBatch(db);

    // 1. Set the order document
    batch.set(orderRef, {
        ...data,
        invoiceNumber: finalInvoiceNumber,
        orderNumber: finalOrderNumber,
        date: effectiveDateStr || undefined,
        invoiceDate: effectiveDateStr || undefined,
        createdAt: createdAtValue,
        updatedAt: serverTimestamp(),
    });

    // 2. Decrement stock for inventory items if order is created as completed
    if (data.status === 'completed' && Array.isArray(data.items)) {
        for (const item of data.items) {
            if (item.productId && item.productId !== 'custom' && !item.productId.startsWith('CUST-')) {
                const productRef = doc(db, 'products', item.productId);
                batch.update(productRef, {
                    stock: increment(-Math.abs(item.quantity)),
                    updatedAt: serverTimestamp(),
                });
            }
        }
    }

    await batch.commit();
    return orderRef.id;
}

export async function updateOrder(id: string, data: Partial<Order>): Promise<void> {
    await updateDoc(doc(db, 'orders', id), { ...data, updatedAt: serverTimestamp() });
}

export async function updateOrderStatus(
    id: string,
    status: string,
    notes?: string
): Promise<void> {
    const updates: any = { status, updatedAt: serverTimestamp() };
    if (notes) updates.notes = notes;
    if (status === 'completed') updates.completedAt = serverTimestamp();

    const orderSnap = await getDoc(doc(db, 'orders', id));
    if (orderSnap.exists()) {
        const order = orderSnap.data() as Order;

        // If transitioning from non-completed to completed: deduct stock
        if (status === 'completed' && order.status !== 'completed' && Array.isArray(order.items)) {
            const batch = writeBatch(db);
            for (const item of order.items) {
                if (item.productId && item.productId !== 'custom' && !item.productId.startsWith('CUST-')) {
                    const productRef = doc(db, 'products', item.productId);
                    batch.update(productRef, {
                        stock: increment(-Math.abs(item.quantity)),
                        updatedAt: serverTimestamp(),
                    });
                }
            }
            batch.update(doc(db, 'orders', id), updates);
            await batch.commit();
            return;
        }

        // If cancelling or returning a previously completed order: restore stock
        if ((status === 'cancelled' || status === 'returned') && order.status === 'completed' && Array.isArray(order.items)) {
            const batch = writeBatch(db);
            for (const item of order.items) {
                if (item.productId && item.productId !== 'custom' && !item.productId.startsWith('CUST-')) {
                    const productRef = doc(db, 'products', item.productId);
                    batch.update(productRef, {
                        stock: increment(Math.abs(item.quantity)),
                        updatedAt: serverTimestamp(),
                    });
                }
            }
            batch.update(doc(db, 'orders', id), updates);
            await batch.commit();
            return;
        }
    }

    await updateDoc(doc(db, 'orders', id), updates);
}

// ======================== PAYMENTS ========================

export const paymentsRef = collection(db, 'payments');

export async function processPayment(data: Omit<Payment, 'id'>): Promise<string> {
    const docRef = await addDoc(paymentsRef, {
        ...data,
        createdAt: serverTimestamp(),
    });
    return docRef.id;
}

export async function getPayments(orderId: string): Promise<Payment[]> {
    const q = query(paymentsRef, where('orderId', '==', orderId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Payment));
}

// ======================== FBR INVOICES ========================

export const fbrInvoicesRef = collection(db, 'fbrInvoices');

export async function getFBRInvoices(status?: string): Promise<FBRInvoiceRecord[]> {
    const snap = await getDocs(query(fbrInvoicesRef, orderBy('createdAt', 'desc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as FBRInvoiceRecord));
    if (status) docs = docs.filter(d => d.status === status);
    return docs;
}

export async function createFBRInvoice(data: Omit<FBRInvoiceRecord, 'id'>): Promise<string> {
    const docRef = await addDoc(fbrInvoicesRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return docRef.id;
}

// ======================== DASHBOARD ========================

export async function getDashboardStats() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const ordersSnap = await getDocs(ordersRef);

    let todayRevenue = 0;
    let todayOrders = 0;
    let activeOrders = 0;
    let totalRevenue = 0;
    const monthlySalesMap: Record<string, number> = {};

    ordersSnap.docs.forEach(d => {
        const order = d.data();
        const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date();
        const isToday = orderDate >= today;

        if (order.status === 'completed') {
            const amount = order.totalAmount || 0;
            totalRevenue += amount;
            if (isToday) {
                todayRevenue += amount;
                todayOrders++;
            }
            const monthKey = orderDate.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
            monthlySalesMap[monthKey] = (monthlySalesMap[monthKey] || 0) + amount;
        } else if (order.status === 'pending') {
            activeOrders++;
            if (isToday) {
                todayOrders++;
            }
        }
    });

    const monthlySales = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const monthKey = d.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
        const shortMonth = d.toLocaleDateString('en-PK', { month: 'short' });
        monthlySales.push({ month: shortMonth, value: monthlySalesMap[monthKey] || 0 });
    }

    // Low stock
    const productsSnap = await getDocs(productsRef);
    let lowStockCount = 0;
    productsSnap.docs.forEach(d => {
        const p = d.data();
        if (p.stock <= (p.minStock || 5) && p.stock > 0) lowStockCount++;
    });

    // Pending FBR
    const fbrSnap = await getDocs(
        query(fbrInvoicesRef, where('status', '==', 'pending'))
    );

    // Accounts Receivable
    const customersSnap = await getDocs(collection(db, 'customers'));
    let receivable = 0;
    customersSnap.docs.forEach(d => {
        receivable += d.data().balance || 0;
    });

    // Accounts Payable
    const suppliersSnap = await getDocs(collection(db, 'suppliers'));
    let payable = 0;
    suppliersSnap.docs.forEach(d => {
        payable += d.data().balance || 0;
    });

    // -------------------------------------------------------------
    // Calculate Monthly Purchases & Total Expenses
    // -------------------------------------------------------------
    let totalExpenses = 0;
    const monthlyPurchasesMap: Record<string, number> = {};

    try {
        // 1. Get Accounts to identify Expense / Purchase accounts
        const accountsSnap = await getDocs(collection(db, 'accounts'));
        const expenseAccIds = new Set();
        const purchaseAccIds = new Set();

        accountsSnap.docs.forEach(d => {
            const acc = d.data();
            if (acc.type === 'expense') {
                expenseAccIds.add(d.id);
                if (acc.name.toLowerCase().includes('purchase') || acc.code === '5001') {
                    purchaseAccIds.add(d.id);
                }
            }
        });

        // 2. Aggregate from Journal Entries (Vouchers, Returns, Manual Entries)
        const journalSnap = await getDocs(collection(db, 'journalEntries'));
        journalSnap.docs.forEach(d => {
            const entry = d.data();
            const amount = (parseFloat(entry.debit) || 0) - (parseFloat(entry.credit) || 0);

            if (expenseAccIds.has(entry.accountId)) {
                totalExpenses += amount;
            }

            if (purchaseAccIds.has(entry.accountId) || entry.accountName === 'Purchases') {
                const entryDate = entry.createdAt?.toDate ? entry.createdAt.toDate() : (entry.date ? new Date(entry.date) : new Date());
                const monthKey = entryDate.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
                monthlyPurchasesMap[monthKey] = (monthlyPurchasesMap[monthKey] || 0) + amount;
            }
        });

        // 3. Aggregate from Purchase Invoices (Document drafts without journal entries)
        const purchaseInvoicesSnap = await getDocs(collection(db, 'purchaseInvoices'));
        purchaseInvoicesSnap.docs.forEach(d => {
            const inv = d.data();
            let invTotal = 0;
            if (inv.items && Array.isArray(inv.items)) {
                inv.items.forEach((item: any) => {
                    const lineTotal = parseFloat(item.total) || (parseFloat(item.unitRate) * parseFloat(item.quantity));
                    invTotal += isNaN(lineTotal) ? 0 : lineTotal;
                });
            }

            totalExpenses += invTotal;

            const invDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : (inv.date ? new Date(inv.date) : new Date());
            const monthKey = invDate.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
            monthlyPurchasesMap[monthKey] = (monthlyPurchasesMap[monthKey] || 0) + invTotal;
        });

    } catch (err) {
        console.error("Error aggregating expenses:", err);
    }

    const monthlyPurchases = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const monthKey = d.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
        const shortMonth = d.toLocaleDateString('en-PK', { month: 'short' });
        monthlyPurchases.push({ month: shortMonth, value: monthlyPurchasesMap[monthKey] || 0 });
    }

    return {
        todayOrders,
        todayRevenue,
        activeOrders,
        totalRevenue,
        totalExpenses,
        monthlySales,
        monthlyPurchases,
        lowStockCount,
        pendingFbr: fbrSnap.size,
        receivable,
        payable,
    };
}

// ======================== ORDER NUMBER GENERATOR ========================

export function generateOrderNumber(): string {
    const ts = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `INV-${ts}-${rand}`;
}

/**
 * Auto-incrementing invoice number using a Firestore counter document.
 * Returns sequential numbers like INV-0001, INV-0002, etc.
 * Uses a transaction for concurrency safety.
 */
export async function getNextInvoiceNumber(): Promise<string> {
    const counterRef = doc(db, 'counters', 'invoiceNumber');
    try {
        const nextNum = await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            let current = 0;
            if (counterDoc.exists()) {
                current = counterDoc.data().current || 0;
            }
            const next = current + 1;
            transaction.set(counterRef, { current: next, updatedAt: serverTimestamp() });
            return next;
        });
        return `INV-${String(nextNum).padStart(4, '0')}`;
    } catch (err) {
        console.error('Failed to get next invoice number, using fallback:', err);
        return generateOrderNumber();
    }
}

// ======================== REAL-TIME LISTENERS ========================

export function onOrdersChange(callback: (orders: Order[]) => void) {
    const q = query(ordersRef, orderBy('createdAt', 'desc'), limit(50));
    return onSnapshot(q, (snap) => {
        callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
    });
}

export function onProductsChange(callback: (products: Product[]) => void) {
    const q = query(productsRef, orderBy('name'));
    return onSnapshot(q, (snap) => {
        callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Product)));
    });
}

// ======================== ACCOUNTING: CHART OF ACCOUNTS ========================

export const accountsRef = collection(db, 'accounts');

const DEFAULT_ACCOUNTS = [
    { code: '1001', name: 'Cash on Hand', type: 'asset' as const, description: 'Physical cash in register' },
    { code: '1002', name: 'Bank Account', type: 'asset' as const, description: 'Business bank account' },
    { code: '1003', name: 'Accounts Receivable', type: 'asset' as const, description: 'Money owed by customers' },
    { code: '2001', name: 'Accounts Payable', type: 'liability' as const, description: 'Money owed to suppliers' },
    { code: '3001', name: 'Owner Equity', type: 'equity' as const, description: 'Owner investment' },
    { code: '4001', name: 'Sales Revenue', type: 'revenue' as const, description: 'Income from sales' },
    { code: '4002', name: 'Sales Returns', type: 'revenue' as const, description: 'Returned sales (contra)' },
    { code: '5001', name: 'Purchases', type: 'expense' as const, description: 'Cost of goods purchased' },
    { code: '5002', name: 'Purchase Returns', type: 'expense' as const, description: 'Returned purchases (contra)' },
    { code: '5003', name: 'General Expenses', type: 'expense' as const, description: 'Miscellaneous expenses' },
    { code: '5004', name: 'Rent Expense', type: 'expense' as const, description: 'Shop rent' },
    { code: '5005', name: 'Utilities', type: 'expense' as const, description: 'Electricity, water, gas' },
    { code: '5006', name: 'Salaries', type: 'expense' as const, description: 'Employee salaries' },
];

export async function seedDefaultAccounts() {
    const snap = await getDocs(query(accountsRef, limit(1)));
    if (!snap.empty) return; // Already seeded
    const batch = writeBatch(db);
    for (const acc of DEFAULT_ACCOUNTS) {
        const ref = doc(accountsRef);
        batch.set(ref, { ...acc, openingBalance: 0, currentBalance: 0, isSystem: true, isActive: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
    await batch.commit();
}

export async function getAccounts(): Promise<any[]> {
    const snap = await getDocs(query(accountsRef, orderBy('code')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createAccount(data: any): Promise<string> {
    const ref = await addDoc(accountsRef, { ...data, currentBalance: data.openingBalance || 0, isSystem: false, isActive: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return ref.id;
}

export async function updateAccount(id: string, data: any): Promise<void> {
    await updateDoc(doc(db, 'accounts', id), { ...data, updatedAt: serverTimestamp() });
}

// ======================== ACCOUNTING: JOURNAL ENTRIES ========================

export const journalEntriesRef = collection(db, 'journalEntries');

export async function postJournalEntry(entries: Array<{ accountId: string; accountName: string; debit: number; credit: number; }>, meta: { date: string; narration: string; referenceType: string; referenceId: string; createdBy: string; }) {
    const batch = writeBatch(db);
    for (const entry of entries) {
        const ref = doc(journalEntriesRef);
        batch.set(ref, { ...entry, ...meta, createdAt: serverTimestamp() });
        // Update account balance
        const accRef = doc(db, 'accounts', entry.accountId);
        const balanceChange = entry.debit - entry.credit;
        batch.update(accRef, { currentBalance: increment(balanceChange), updatedAt: serverTimestamp() });
    }
    await batch.commit();
}

export async function getJournalEntries(filters?: { accountId?: string; date?: string; referenceType?: string }): Promise<any[]> {
    const snap = await getDocs(query(journalEntriesRef, orderBy('createdAt', 'desc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
    if (filters?.accountId) docs = docs.filter(d => d.accountId === filters.accountId);
    if (filters?.date) docs = docs.filter(d => d.date === filters.date);
    if (filters?.referenceType) docs = docs.filter(d => d.referenceType === filters.referenceType);
    return docs;
}

export async function getAccountLedger(accountId: string): Promise<any[]> {
    const snap = await getDocs(query(journalEntriesRef, orderBy('createdAt', 'asc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
    docs = docs.filter(d => d.accountId === accountId);
    let runningBalance = 0;
    return docs.map(d => {
        runningBalance += (d.debit || 0) - (d.credit || 0);
        return { ...d, runningBalance };
    });
}

export async function getDayBook(date: string): Promise<any[]> {
    const snap = await getDocs(query(journalEntriesRef, orderBy('createdAt', 'asc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
    return docs.filter(d => d.date === date);
}

export async function getTrialBalance(): Promise<any[]> {
    const accounts = await getAccounts();
    return accounts.filter(a => a.isActive).map(a => ({
        id: a.id,
        code: a.code,
        name: a.name,
        type: a.type,
        debit: a.currentBalance > 0 ? a.currentBalance : 0,
        credit: a.currentBalance < 0 ? Math.abs(a.currentBalance) : 0,
    }));
}

// ======================== ACCOUNTING: VOUCHERS ========================

export const vouchersRef = collection(db, 'vouchers');

export function generateVoucherNumber(type: string): string {
    const prefix = type === 'cash_payment' ? 'CPV' : type === 'cash_receipt' ? 'CRV' : type === 'bank_receipt' ? 'BRV' : 'GV';
    return `${prefix}-${Date.now().toString(36).toUpperCase()}`;
}

export async function getVouchers(voucherType?: string): Promise<any[]> {
    const snap = await getDocs(query(vouchersRef, orderBy('createdAt', 'desc')));
    let docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
    if (voucherType) {
        docs = docs.filter(d => d.voucherType === voucherType);
    }
    return docs;
}

export async function createVoucher(data: any): Promise<string> {
    const ref = await addDoc(vouchersRef, { ...data, createdAt: serverTimestamp() });
    // Auto-post journal entries
    await postJournalEntry([
        { accountId: data.debitAccountId, accountName: data.debitAccountName || '', debit: data.amount, credit: 0 },
        { accountId: data.creditAccountId, accountName: data.creditAccountName || '', debit: 0, credit: data.amount },
    ], { date: data.date, narration: data.narration, referenceType: data.voucherType, referenceId: ref.id, createdBy: data.createdBy });
    return ref.id;
}

// ======================== ACCOUNTING: SALES RETURNS ========================

export const salesReturnsRef = collection(db, 'salesReturns');

export function generateReturnNumber(type: 'SR' | 'PR'): string {
    return `${type}-${Date.now().toString(36).toUpperCase()}`;
}

export async function getSalesReturns(): Promise<any[]> {
    const snap = await getDocs(query(salesReturnsRef, orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createSalesReturn(data: any, accounts: { salesReturnsAccId: string; cashAccId: string; receivableAccId: string; }): Promise<string> {
    const ref = await addDoc(salesReturnsRef, { ...data, status: 'completed', createdAt: serverTimestamp() });
    // Journal: Debit Sales Returns, Credit Cash or Receivable
    const creditAccId = data.refundMethod === 'cash' ? accounts.cashAccId : accounts.receivableAccId;
    const creditAccName = data.refundMethod === 'cash' ? 'Cash on Hand' : 'Accounts Receivable';
    await postJournalEntry([
        { accountId: accounts.salesReturnsAccId, accountName: 'Sales Returns', debit: data.totalAmount, credit: 0 },
        { accountId: creditAccId, accountName: creditAccName, debit: 0, credit: data.totalAmount },
    ], { date: data.date, narration: `Sales Return ${data.returnNumber} — ${data.customerName}`, referenceType: 'sales_return', referenceId: ref.id, createdBy: data.createdBy });
    // Restore stock
    const batch = writeBatch(db);
    for (const item of data.items) {
        if (item.productId) {
            batch.update(doc(db, 'products', item.productId), { stock: increment(item.quantity) });
        }
    }
    await batch.commit();
    return ref.id;
}

// ======================== ACCOUNTING: PURCHASE RETURNS ========================

export const purchaseReturnsRef = collection(db, 'purchaseReturns');

export async function getPurchaseReturns(): Promise<any[]> {
    const snap = await getDocs(query(purchaseReturnsRef, orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createPurchaseReturn(data: any, accounts: { purchaseReturnsAccId: string; payableAccId: string; }): Promise<string> {
    const ref = await addDoc(purchaseReturnsRef, { ...data, status: 'completed', createdAt: serverTimestamp() });
    // Journal: Debit Payable, Credit Purchase Returns
    await postJournalEntry([
        { accountId: accounts.payableAccId, accountName: 'Accounts Payable', debit: data.totalAmount, credit: 0 },
        { accountId: accounts.purchaseReturnsAccId, accountName: 'Purchase Returns', debit: 0, credit: data.totalAmount },
    ], { date: data.date, narration: `Purchase Return ${data.returnNumber} — ${data.supplierName}`, referenceType: 'purchase_return', referenceId: ref.id, createdBy: data.createdBy });
    // Reduce stock
    const batch = writeBatch(db);
    for (const item of data.items) {
        if (item.productId) {
            batch.update(doc(db, 'products', item.productId), { stock: increment(-item.quantity) });
        }
    }
    await batch.commit();
    return ref.id;
}

// ================= Quotations =================

export function generateQuotationNumber(): string {
    const year = new Date().getFullYear();
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `QT-${year}-${rand}`;
}

export const getQuotations = async (filters?: { status?: string }): Promise<Quotation[]> => {
    try {
        const q = query(collection(db, 'quotations'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        let docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Quotation));
        if (filters?.status && filters.status !== 'all') {
            docs = docs.filter(d => d.status === filters.status);
        }
        return docs;
    } catch (e: any) {
        throw new Error('Error fetching quotations: ' + e.message);
    }
}

export const getQuotation = async (id: string): Promise<Quotation | null> => {
    try {
        const snap = await getDoc(doc(db, 'quotations', id));
        return snap.exists() ? ({ id: snap.id, ...snap.data() } as Quotation) : null;
    } catch (e: any) {
        throw new Error('Error fetching quotation: ' + e.message);
    }
}

export const saveQuotation = async (data: Partial<Quotation> & { quotationNumber: string }): Promise<string> => {
    try {
        if (data.id) {
            const quotationRef = doc(db, 'quotations', data.id);
            const { id, ...updateData } = data;
            await updateDoc(quotationRef, {
                ...updateData,
                updatedAt: serverTimestamp(),
            });
            return data.id;
        } else {
            const payload = {
                ...data,
                status: data.status || 'draft',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const res = await addDoc(collection(db, 'quotations'), payload);
            return res.id;
        }
    } catch (e: any) {
        throw new Error('Error saving quotation: ' + e.message);
    }
}

export const updateQuotationStatus = async (id: string, status: Quotation['status']): Promise<void> => {
    try {
        await updateDoc(doc(db, 'quotations', id), {
            status,
            updatedAt: serverTimestamp(),
        });
    } catch (e: any) {
        throw new Error('Error updating quotation status: ' + e.message);
    }
}

export const convertQuotationToOrder = async (quotation: Quotation, createdBy: string): Promise<string> => {
    try {
        // Map quotation items to order items
        const orderItems = quotation.items.map(it => {
            const unitPrice = typeof it.unitRate === 'number' ? it.unitRate : parseFloat(String(it.unitRate)) || 0;
            const lineTotal = typeof it.total === 'number' ? it.total : parseFloat(String(it.total)) || (unitPrice * it.quantity);
            const taxRate = it.taxRate || 0;
            const taxAmount = (lineTotal * taxRate) / 100;

            return {
                productId: it.productId || 'custom',
                name: it.description,
                code: it.itemCode || 'GEN-01',
                hsCode: it.hsCode || '',
                uom: it.uom || 'PCS',
                quantity: it.quantity || 1,
                unitPrice: unitPrice,
                total: lineTotal,
                taxRate: taxRate,
                taxAmount: taxAmount,
            };
        });

        const invoiceNum = await getNextInvoiceNumber();

        const orderData: Omit<Order, 'id'> = {
            orderNumber: invoiceNum,
            invoiceNumber: invoiceNum,
            customerId: quotation.customerId || undefined,
            customerName: quotation.companyName || quotation.customerName || 'Direct Customer',
            customerAddress: quotation.companyAddress,
            customerPhone: quotation.customerPhone,
            customerNtn: quotation.customerNtn,
            customerStn: quotation.customerStn,
            createdBy: createdBy || 'admin',
            status: 'completed',
            items: orderItems,
            subtotal: quotation.subtotal || 0,
            taxRate: quotation.taxRate || 0,
            taxAmount: quotation.taxAmount || 0,
            discountAmount: quotation.discountAmount || 0,
            totalAmount: quotation.totalAmount || 0,
            paymentMethod: 'cash',
            notes: `Converted from Quotation #${quotation.quotationNumber}. ${quotation.notes || ''}`.trim(),
        };

        // Create the order
        const orderId = await createOrder(orderData);

        // Update the quotation status and reference the order
        if (quotation.id) {
            await updateDoc(doc(db, 'quotations', quotation.id), {
                status: 'converted',
                convertedOrderId: orderId,
                updatedAt: serverTimestamp(),
            });
        }

        return orderId;
    } catch (e: any) {
        throw new Error('Error converting quotation to order: ' + e.message);
    }
}

export const deleteQuotation = async (id: string) => {
    try {
        await deleteDoc(doc(db, 'quotations', id));
    } catch (e: any) {
        throw new Error('Error deleting quotation: ' + e.message);
    }
}

// ==========================================
// PURCHASE INVOICES
// ==========================================

export interface PurchaseInvoiceItem {
    sNo?: number
    productId?: string
    description?: string
    name?: string
    brand?: string
    itemCode?: string
    code?: string
    hsCode?: string
    uom?: string
    unitRate?: string | number
    price?: number
    quantity: number
    taxRate?: number
    taxAmount?: number
    total?: string | number
}

export interface PurchaseInvoiceDocumentRecord {
    id?: string
    purchaseInvoiceNumber: string
    docNumber?: string
    date: string
    invoiceDate?: string
    supplierId?: string
    supplierName?: string
    attnName1?: string
    attnRole1?: string
    attnName2?: string
    attnRole2?: string
    companyName?: string
    companyAddress?: string
    subject?: string
    items: PurchaseInvoiceItem[]
    subtotal?: number
    taxRate?: number
    taxAmount?: number
    discountAmount?: number
    totalAmount?: number
    notes?: string
    status?: string
    createdBy?: string
    createdAt?: any
    updatedAt?: any
}

export async function getNextPurchaseInvoiceNumber(): Promise<string> {
    const counterRef = doc(db, 'counters', 'purchaseInvoiceNumber');
    try {
        const nextNum = await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            let current = 0;
            if (counterDoc.exists()) {
                current = counterDoc.data().current || 0;
            }
            const next = current + 1;
            transaction.set(counterRef, { current: next, updatedAt: serverTimestamp() });
            return next;
        });
        return `PINV-${String(nextNum).padStart(4, '0')}`;
    } catch (err) {
        console.error('Failed to get next purchase invoice number, using fallback:', err);
        return `PINV-${Date.now().toString().slice(-4)}`;
    }
}

export const savePurchaseInvoice = async (data: Partial<PurchaseInvoiceDocumentRecord> & { purchaseInvoiceNumber: string }): Promise<string> => {
    const batch = writeBatch(db);
    const invoiceRef = data.id ? doc(db, 'purchaseInvoices', data.id) : doc(collection(db, 'purchaseInvoices'));

    const effectiveDateStr = data.invoiceDate || data.date;
    let createdAtValue = data.createdAt;
    if (!createdAtValue && effectiveDateStr) {
        const parts = effectiveDateStr.split('T')[0].split('-').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
            createdAtValue = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
        }
    }
    if (!createdAtValue) {
        createdAtValue = serverTimestamp();
    }

    batch.set(invoiceRef, {
        ...data,
        companyName: data.companyName || data.supplierName || 'General Supplier',
        supplierName: data.supplierName || data.companyName || 'General Supplier',
        date: effectiveDateStr || undefined,
        invoiceDate: effectiveDateStr || undefined,
        createdAt: createdAtValue,
        updatedAt: serverTimestamp(),
    }, { merge: true });

    // Increment stock for inventory items if this is a newly created purchase record
    if (!data.id && Array.isArray(data.items)) {
        for (const item of data.items) {
            if (item.productId && item.productId !== 'custom' && !item.productId.startsWith('CUST-')) {
                const productRef = doc(db, 'products', item.productId);
                batch.update(productRef, {
                    stock: increment(Math.abs(item.quantity)),
                    updatedAt: serverTimestamp(),
                });
            }
        }
    }

    await batch.commit();
    return invoiceRef.id;
}

export const getPurchaseInvoices = async () => {
    const q = query(collection(db, 'purchaseInvoices'), orderBy('createdAt', 'desc'))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

export const deletePurchaseInvoice = async (id: string) => {
    await deleteDoc(doc(db, 'purchaseInvoices', id))
}

// ==========================================
// COMPANY & INVOICE SETTINGS
// ==========================================

export interface CompanySettings {
    companyName: string
    tagline?: string
    address: string
    city: string
    province?: string
    country?: string
    phone?: string
    email?: string
    website?: string
    ntn: string
    stn: string
    posId?: string
    invoiceTitle?: string
    invoiceFooterNotes?: string
    termsAndConditions?: string
    logoUrl?: string
    updatedAt?: any
    updatedBy?: string
}

export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
    companyName: 'DYNAMIC INDUSTRIAL SOLUTIONS',
    tagline: 'Engineering & Industrial Supplies',
    address: 'SUIT NO. 411 BLOCK-F1, MAIN BOULEVARD PAK ARAB SOCIETY, 18-KM FEROZEPUR ROAD',
    city: 'Lahore',
    province: 'Punjab',
    country: 'Pakistan',
    phone: '+92 300 4113099',
    email: 'info@dynamicindustrialsolutions.online',
    website: 'https://dynamicindustrialsolutions.online',
    ntn: '4113099',
    stn: '0300411300919',
    posId: '1',
    invoiceTitle: 'Sales Tax Invoice',
    invoiceFooterNotes: 'Thank you for your business!',
    termsAndConditions: '1. Prices and terms are subject to standard commercial agreement.\n2. In case of discrepancies, please report within 7 days.\n3. Goods sold are non-refundable unless defective.',
}

export const getCompanySettings = async (): Promise<CompanySettings> => {
    try {
        const ref = doc(db, 'settings', 'company')
        const snap = await getDoc(ref)
        if (snap.exists()) {
            return { ...DEFAULT_COMPANY_SETTINGS, ...(snap.data() as CompanySettings) }
        }
        return DEFAULT_COMPANY_SETTINGS
    } catch (e: any) {
        console.warn('Could not fetch company settings from Firestore, using defaults:', e)
        return DEFAULT_COMPANY_SETTINGS
    }
}

export const saveCompanySettings = async (settings: Partial<CompanySettings>, updatedBy?: string): Promise<void> => {
    try {
        const ref = doc(db, 'settings', 'company')
        await setDoc(ref, {
            ...DEFAULT_COMPANY_SETTINGS,
            ...settings,
            updatedAt: serverTimestamp(),
            updatedBy: updatedBy || 'admin',
        }, { merge: true })
    } catch (e: any) {
        throw new Error('Error saving company settings: ' + e.message)
    }
}
